import React, { useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import { mdiPencilOutline } from '@mdi/js'
import { Formik, FormikHelpers } from 'formik'

import { JobDetailsContactMessages } from './messages'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { jobInfoContactsSelector } from 'features/jobs/selectors'
import { EmailField, TextField } from 'components/Fields'
import { commonMessages } from 'app/i18n/messages'
import { SpinnerCentered } from 'components/Loader'
import { fetchJobInfoThunk, updateJobThunk } from 'features/jobs/slice'
import { JobPageContext } from 'features/jobs/JobPageContext'
import type { JobContacts } from 'features/jobs/types'
import {
  JobDetailsContactButtonsWrapper,
  JobDetailsContactCancelButton,
  JobDetailsContactContainer,
  JobDetailsContactEditButton,
  JobDetailsContactForm,
  JobDetailsContactFormContent,
  JobDetailsContactFormControl,
  JobDetailsContactHeader,
  JobDetailsContactStyledIcon,
  JobDetailsContactSubmitButton,
  JobDetailsContactTitle,
} from './styles'

function JobDetailsContact() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const { jobId } = useContext(JobPageContext)
  const initialContacts = useAppSelector(jobInfoContactsSelector)
  const [editMode, setEditMode] = useState<boolean>(false)

  const handleSubmit = async (
    values: JobContacts,
    { setSubmitting, setStatus }: FormikHelpers<JobContacts>
  ) => {
    try {
      setSubmitting(true)
      await dispatch(
        updateJobThunk({ domainId: jobId, payload: { contacts: [values] } })
      )
      await dispatch(fetchJobInfoThunk(jobId))
    } catch (err: any) {
      setStatus({ error: true })
      console.log('Error updating job', err)
    } finally {
      setSubmitting(false)
      setEditMode(false)
    }
  }

  return (
    <JobDetailsContactContainer>
      <Formik
        initialValues={initialContacts}
        initialStatus={{ error: false }}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => (
          <>
            <JobDetailsContactHeader>
              <JobDetailsContactTitle>
                {formatMessage(JobDetailsContactMessages.title)}
              </JobDetailsContactTitle>

              {!editMode && (
                <JobDetailsContactEditButton
                  onClick={() => {
                    formikProps.setStatus({ error: false })
                    setEditMode(true)
                  }}
                >
                  <JobDetailsContactStyledIcon path={mdiPencilOutline} />
                </JobDetailsContactEditButton>
              )}
            </JobDetailsContactHeader>
            <JobDetailsContactForm>
              <JobDetailsContactFormContent>
                <TextField
                  name="name"
                  placeholder={formatMessage(
                    JobDetailsContactMessages.nameField
                  )}
                  disabled={!editMode}
                  showLabel
                />
                <TextField
                  name="role"
                  placeholder={formatMessage(
                    JobDetailsContactMessages.roleField
                  )}
                  disabled={!editMode}
                  showLabel
                />
                <TextField
                  name="phone"
                  placeholder={formatMessage(
                    JobDetailsContactMessages.phoneField
                  )}
                  disabled={!editMode}
                  showLabel
                />
                <EmailField
                  name="email"
                  placeholder={formatMessage(
                    JobDetailsContactMessages.emailField
                  )}
                  disabled={!editMode}
                  showLabel
                />
              </JobDetailsContactFormContent>

              {formikProps.status.error && (
                <JobDetailsContactTitle>
                  {formatMessage(commonMessages.commonError)}
                </JobDetailsContactTitle>
              )}

              {editMode && (
                <JobDetailsContactFormControl>
                  {formikProps.isSubmitting && (
                    <JobDetailsContactButtonsWrapper>
                      <SpinnerCentered size="40" />
                    </JobDetailsContactButtonsWrapper>
                  )}
                  {!formikProps.isSubmitting && (
                    <JobDetailsContactButtonsWrapper>
                      <JobDetailsContactCancelButton
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          formikProps.resetForm()
                          setEditMode(false)
                        }}
                      >
                        {formatMessage(commonMessages.cancel)}
                      </JobDetailsContactCancelButton>
                      <JobDetailsContactSubmitButton
                        variant="primary"
                        type="submit"
                      >
                        {formatMessage(commonMessages.save)}
                      </JobDetailsContactSubmitButton>
                    </JobDetailsContactButtonsWrapper>
                  )}
                </JobDetailsContactFormControl>
              )}
            </JobDetailsContactForm>
          </>
        )}
      </Formik>
    </JobDetailsContactContainer>
  )
}

export default JobDetailsContact
